import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, CheckCircle2, XCircle, Clock, Loader2 } from 'lucide-react';
import api from '../../lib/api';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../../components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ConfirmButton } from '../StaffAppItemDetail';
import ToolHeader, { Spinner } from './ToolHeader';

const range = (r) => `${r.start_date} → ${r.end_date} · ${r.days} day${r.days === 1 ? '' : 's'}`;
const prettyType = (t = 'annual') => t.charAt(0).toUpperCase() + t.slice(1);
const todayISO = () => new Date().toISOString().slice(0, 10);

const LEAVE_TYPES = [
  { v: 'annual', label: 'Annual' },
  { v: 'sick', label: 'Sick' },
  { v: 'maternity', label: 'Maternity' },
  { v: 'unpaid', label: 'Unpaid' },
  { v: 'other', label: 'Other' },
];

const STATUS_META = {
  pending: { icon: Clock, cls: 'text-amber-600', label: 'Waiting' },
  approved: { icon: CheckCircle2, cls: 'text-emerald-600', label: 'Approved' },
  rejected: { icon: XCircle, cls: 'text-destructive', label: 'Rejected' },
  cancelled: { icon: XCircle, cls: 'text-muted-foreground', label: 'Cancelled' },
};

function StatusPill({ status }) {
  const m = STATUS_META[status] || STATUS_META.pending;
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-semibold ${m.cls}`}>
      <Icon className="w-3.5 h-3.5" /> {m.label}
    </span>
  );
}

function ApproveCard({ r, canDecide, onDone }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const decide = async (status) => {
    setBusy(true);
    try {
      await api.put(`/hr/leave/${r.leave_id}/decision`, { status, decision_note: note });
      toast.success(`Leave ${status} for ${r.employee_name}`);
      onDone();
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not save decision'); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-border bg-card p-3.5 space-y-2.5" data-testid={`staff-app-leave-${r.leave_id}`}>
      <div>
        <p className="text-sm font-semibold">{r.employee_name}{r.job_title ? <span className="text-muted-foreground font-normal"> · {r.job_title}</span> : null}</p>
        <p className="text-xs text-muted-foreground"><span className="capitalize">{r.leave_type}</span> leave · {range(r)}</p>
        {r.reason && <p className="text-xs mt-1">“{r.reason}”</p>}
      </div>
      {canDecide ? (
        <>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note to employee (optional)" rows={2} data-testid={`staff-app-leave-note-${r.leave_id}`} />
          <div className="flex gap-2">
            <ConfirmButton label="Reject" variant="outline" loading={busy} testId={`staff-app-leave-reject-${r.leave_id}`} onConfirm={() => decide('rejected')}
              title="Reject this leave request?" description={`${r.employee_name} — ${range(r)}.`} />
            <ConfirmButton label="Approve" loading={busy} testId={`staff-app-leave-approve-${r.leave_id}`} onConfirm={() => decide('approved')}
              title="Approve this leave request?" description={`${r.employee_name} — ${r.leave_type} leave, ${range(r)}.`} />
          </div>
        </>
      ) : <p className="text-xs text-muted-foreground">View only — an HR manager decides.</p>}
    </div>
  );
}

function MyLeaveCard({ r }) {
  return (
    <div className="rounded-xl border border-border bg-card p-3 space-y-1" data-testid={`staff-app-my-leave-${r.leave_id}`}>
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-semibold"><span className="capitalize">{r.leave_type}</span> · {range(r)}</p>
        <StatusPill status={r.status} />
      </div>
      {r.reason && <p className="text-xs text-muted-foreground">“{r.reason}”</p>}
      {r.decision_note && (
        <p className="text-xs mt-1"><span className="font-semibold">Manager note:</span> {r.decision_note}</p>
      )}
    </div>
  );
}

function RequestLeaveSheet({ open, onOpenChange, onCreated }) {
  const [leaveType, setLeaveType] = useState('annual');
  const [startDate, setStartDate] = useState(todayISO());
  const [endDate, setEndDate] = useState(todayISO());
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const reset = () => { setLeaveType('annual'); setStartDate(todayISO()); setEndDate(todayISO()); setReason(''); };

  const submit = async (e) => {
    e.preventDefault();
    if (!startDate || !endDate) return toast.error('Pick start and end dates');
    if (endDate < startDate) return toast.error('End date must be on or after start date');
    setBusy(true);
    try {
      await api.post('/staff-app/my-leave/request', {
        leave_type: leaveType, start_date: startDate, end_date: endDate, reason: reason.trim(),
      });
      toast.success('Leave request sent — managers have been notified');
      reset();
      onOpenChange(false);
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send leave request');
    } finally { setBusy(false); }
  };

  const days = startDate && endDate && endDate >= startDate
    ? Math.round((new Date(`${endDate}T00:00:00`) - new Date(`${startDate}T00:00:00`)) / 86400000) + 1
    : 0;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[90vh] overflow-y-auto" data-testid="staff-app-leave-request-sheet">
        <SheetHeader className="text-left">
          <SheetTitle>Request leave</SheetTitle>
          <SheetDescription>Your admins at this branch will get a push notification right away.</SheetDescription>
        </SheetHeader>
        <form onSubmit={submit} className="pt-3 space-y-3">
          <div>
            <Label>Leave type</Label>
            <Select value={leaveType} onValueChange={setLeaveType}>
              <SelectTrigger data-testid="staff-app-leave-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {LEAVE_TYPES.map((t) => (
                  <SelectItem key={t.v} value={t.v} data-testid={`staff-app-leave-type-${t.v}`}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label htmlFor="leave-start">Start</Label>
              <Input id="leave-start" type="date" value={startDate} min={todayISO()} onChange={(e) => setStartDate(e.target.value)}
                data-testid="staff-app-leave-start" />
            </div>
            <div>
              <Label htmlFor="leave-end">End</Label>
              <Input id="leave-end" type="date" value={endDate} min={startDate || todayISO()} onChange={(e) => setEndDate(e.target.value)}
                data-testid="staff-app-leave-end" />
            </div>
          </div>
          {days > 0 && <p className="text-xs text-muted-foreground" data-testid="staff-app-leave-days">That's {days} day{days === 1 ? '' : 's'} off.</p>}
          <div>
            <Label htmlFor="leave-reason">Reason (optional)</Label>
            <Textarea id="leave-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
              placeholder="Anything you'd like your manager to know" data-testid="staff-app-leave-reason" />
          </div>
          <Button type="submit" disabled={busy} className="w-full h-11" data-testid="staff-app-leave-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send request'}
          </Button>
        </form>
      </SheetContent>
    </Sheet>
  );
}

export default function LeavePage() {
  const { tools, reloadFeed } = useOutletContext();
  const [queue, setQueue] = useState(null);
  const [mine, setMine] = useState(null);
  const [open, setOpen] = useState(false);

  const loadMine = () => api.get('/staff-app/my-leave').then((r) => setMine(r.data)).catch(() => setMine({ linked: false, pending: [], decided: [] }));
  const loadQueue = () => api.get('/staff-app/leave').then((r) => setQueue(r.data)).catch(() => setQueue({ pending: [], recent: [], can_decide: false }));
  const reloadAll = () => { loadMine(); if (tools?.leave_view) loadQueue(); };

  useEffect(() => {
    if (!tools) return;
    loadMine();
    if (tools.leave_view) loadQueue(); else setQueue({ pending: [], recent: [], can_decide: false });
  }, [tools]);

  // Clear "new decision" badge when opening the page.
  useEffect(() => {
    if (mine && mine.unseen_decisions > 0) {
      api.post('/staff-app/my-leave/seen').then(() => reloadFeed && reloadFeed()).catch(() => {});
    }
  }, [mine, reloadFeed]);

  const loading = !tools || mine === null || queue === null;
  const canDecide = !!queue?.can_decide;
  const canRequest = !!mine?.linked;
  const pendingCount = (queue?.pending?.length || 0) + (mine?.pending?.length || 0);

  return (
    <div data-testid="staff-app-leave-page">
      <ToolHeader
        title="Leave"
        subtitle={loading ? '' : (canDecide ? `${queue.pending.length} waiting · ${mine?.pending?.length || 0} of yours` : `${mine?.pending?.length || 0} waiting · ${mine?.decided?.length || 0} decided`)}
        right={canRequest ? (
          <button onClick={() => setOpen(true)} data-testid="staff-app-leave-request-open"
            className="inline-flex items-center gap-1 text-xs font-semibold text-primary border border-primary/30 rounded-full px-2.5 py-1.5 hover:bg-primary/10">
            <Plus className="w-3.5 h-3.5" /> Request
          </button>
        ) : null}
      />
      {loading ? <Spinner /> : (
        <div className="p-4 space-y-5">
          {!canRequest && !canDecide && (
            <p className="py-6 text-center text-sm text-muted-foreground" data-testid="staff-app-leave-notlinked">
              Your account isn't linked to an employee record. Ask your admin to link it so you can request leave.
            </p>
          )}

          {/* My own requests — everyone with a linked record */}
          {canRequest && (
            <section className="space-y-2" data-testid="staff-app-my-leave-section">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">My requests</p>
              {mine.pending.length === 0 && mine.decided.length === 0 && (
                <p className="py-4 text-center text-sm text-muted-foreground" data-testid="staff-app-my-leave-empty">
                  You haven't requested any leave yet. Tap “Request” above to send one.
                </p>
              )}
              {mine.pending.map((r) => <MyLeaveCard key={r.leave_id} r={r} />)}
              {mine.decided.map((r) => <MyLeaveCard key={r.leave_id} r={r} />)}
            </section>
          )}

          {/* Approver queue — managers */}
          {tools?.leave_view && (
            <section className="space-y-2" data-testid="staff-app-leave-queue-section">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">To decide</p>
              {queue.pending.length === 0 && (
                <p className="py-4 text-center text-sm text-muted-foreground" data-testid="staff-app-leave-empty">
                  No leave requests waiting.
                </p>
              )}
              {queue.pending.map((r) => <ApproveCard key={r.leave_id} r={r} canDecide={canDecide} onDone={reloadAll} />)}
              {queue.recent.length > 0 && (
                <div className="pt-2 space-y-1.5" data-testid="staff-app-leave-recent">
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Recently decided</p>
                  {queue.recent.map((r) => (
                    <div key={r.leave_id} className="flex justify-between gap-2 text-xs py-1.5 border-b border-border">
                      <span className="truncate">{r.employee_name} · {range(r)}</span>
                      <span className={`capitalize font-semibold ${r.status === 'approved' ? 'text-emerald-600' : 'text-muted-foreground'}`}>{r.status}</span>
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}
        </div>
      )}
      <RequestLeaveSheet open={open} onOpenChange={setOpen} onCreated={reloadAll} />
    </div>
  );
}
